import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { familyApi, readerApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CreateFamilyForm from '../components/family/CreateFamilyForm';
import FamilyPlanEditor from '../components/family/FamilyPlanEditor';
import FamilyPlanSummary from '../components/family/FamilyPlanSummary';
import { memberRows, planHero } from '../components/family/familyPlanView';
import LoadingSpinner from '../components/LoadingSpinner';
import ReadersPage from './ReadersPage';
import '../components/progress/ProgressContextRow.css';
import './FamilyPage.css';

const FamilyPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const showReaders = location.hash === '#readers';

  const [families, setFamilies] = useState([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState(null);
  const [planFamilyId, setPlanFamilyId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [myReaders, setMyReaders] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showAddReader, setShowAddReader] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteNote, setInviteNote] = useState('');
  const [joinCode, setJoinCode] = useState('');

  const family = families.find((item) => item.id === selectedFamilyId) || null;
  const isOwner = Boolean(family && user?.id && family.createdBy === user.id);
  const ownerName = family?.members?.find((member) => member.id === family.createdBy)?.name || 'the family owner';
  const planReady = planFamilyId === selectedFamilyId;
  const familyReaderIds = new Set((family?.readers || []).map((reader) => reader.id));
  const myReadersInFamily = myReaders
    .filter((reader) => familyReaderIds.has(reader.id))
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  const readersToAdd = myReaders.filter((reader) => !familyReaderIds.has(reader.id));
  const myReaderIds = myReadersInFamily.map((reader) => reader.id);
  const hero = planHero(plan, myReaderIds);
  const rows = memberRows({ family, stats, plan, userId: user?.id, myReaderIds });
  const totals = stats?.totals;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [familiesResponse, invitesResponse, readersResponse] = await Promise.all([
          familyApi.getFamilies(),
          familyApi.getInvites().catch(() => ({ data: { invites: [] } })),
          readerApi.getReaders().catch(() => ({ data: { readers: [] } }))
        ]);
        if (cancelled) return;
        const next = familiesResponse.data.families || [];
        setFamilies(next);
        setInvites(invitesResponse.data.invites || []);
        setMyReaders(readersResponse.data.readers || []);
        setSelectedFamilyId((current) => (
          current && next.some((item) => item.id === current) ? current : (next[0]?.id || null)
        ));
        setError(null);
      } catch (err) {
        console.error('Error loading families:', err);
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load families');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  useEffect(() => {
    if (!selectedFamilyId) {
      setPlan(null);
      setPlanError(null);
      setPlanFamilyId(null);
      setStats(null);
      setStatsError(null);
      return undefined;
    }
    let cancelled = false;
    const loadPlan = async () => {
      const [planResult, statsResult] = await Promise.allSettled([
        familyApi.getPlan(selectedFamilyId),
        familyApi.getStats(selectedFamilyId)
      ]);
      if (cancelled) return;
      if (planResult.status === 'fulfilled') {
        setPlan(planResult.value.data);
        setPlanError(null);
      } else {
        console.error('Error loading family plan:', planResult.reason);
        setPlan(null);
        setPlanError('The family plan could not be loaded.');
      }
      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value.data);
        setStatsError(null);
      } else {
        console.error('Error loading family stats:', statsResult.reason);
        setStats(null);
        setStatsError('Family stats could not be loaded.');
      }
      setPlanFamilyId(selectedFamilyId);
    };
    loadPlan();
    return () => {
      cancelled = true;
    };
  }, [selectedFamilyId, reloadToken]);

  useEffect(() => {
    if (!showInvite || !family?.id) return undefined;
    const query = inviteQuery.trim();
    if (query.length < 2) {
      setInviteResults([]);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const response = await familyApi.searchPeople(family.id, query);
        if (!cancelled) setInviteResults(response.data.users || []);
      } catch (err) {
        console.error('Error searching people:', err);
        if (!cancelled) setInviteResults([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [inviteQuery, showInvite, family?.id]);

  const reload = () => setReloadToken((current) => current + 1);

  const savePlan = async (draft, close) => {
    if (!family) return;
    try {
      const response = await familyApi.savePlan(family.id, draft);
      setPlan(response.data);
      setError(null);
      if (close) setShowEditor(false);
    } catch (err) {
      console.error('Error saving family plan:', err);
      setError(err.response?.data?.error || 'Failed to save the plan');
    }
  };

  const copyInviteCode = async () => {
    if (!family?.inviteCode) return;
    try {
      await navigator.clipboard.writeText(family.inviteCode);
      setInviteNote('Copied');
    } catch (err) {
      console.error('Error copying invite code:', err);
      setInviteNote('Could not copy the code');
    }
  };

  const invitePerson = async (person) => {
    if (!family) return;
    try {
      await familyApi.invitePerson(family.id, person.id);
      setInviteNote(`Invited ${person.name}`);
      setInviteResults((current) => current.filter((item) => item.id !== person.id));
    } catch (err) {
      console.error('Error inviting person:', err);
      setError(err.response?.data?.error || 'Failed to send the invite');
    }
  };

  const submitCode = async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return;
    try {
      await familyApi.joinFamily(trimmed);
      setJoinCode('');
      setError(null);
      reload();
    } catch (err) {
      console.error('Error joining family:', err);
      setError(err.response?.data?.error || 'Failed to join family');
    }
  };

  const transferOwner = async (member) => {
    if (!family) return;
    if (!window.confirm(`Make ${member.name} the owner of ${family.name}? You will no longer manage this family.`)) return;
    try {
      await familyApi.transferOwner(family.id, member.id);
      setError(null);
      reload();
    } catch (err) {
      console.error('Error transferring ownership:', err);
      setError(err.response?.data?.error || 'Failed to transfer ownership');
    }
  };

  const removeMember = async (member) => {
    if (!family) return;
    if (!window.confirm(`Remove ${member.name} from ${family.name}?`)) return;
    try {
      await familyApi.removeMember(family.id, member.id);
      setError(null);
      reload();
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.response?.data?.error || 'Failed to remove that person');
    }
  };

  const leaveFamily = async () => {
    if (!family) return;
    if (!window.confirm('Are you sure you want to leave this family?')) return;
    try {
      await familyApi.leaveFamily(family.id);
      setSelectedFamilyId(null);
      reload();
    } catch (err) {
      console.error('Error leaving family:', err);
      setError(err.response?.data?.error || 'Failed to leave family');
    }
  };

  const deleteFamily = async () => {
    if (!family) return;
    if (!window.confirm(`Are you sure you want to delete the family "${family.name}"?\n\nThis will permanently delete the family group, but all readers will remain active and can be added to other families.`)) return;
    try {
      await familyApi.deleteFamily(family.id);
      setSelectedFamilyId(null);
      reload();
    } catch (err) {
      console.error('Error deleting family:', err);
      setError(err.response?.data?.error || 'Failed to delete family');
    }
  };

  const addReader = async (readerId) => {
    if (!family) return;
    try {
      await familyApi.addReaderToFamily(family.id, readerId);
      setError(null);
      reload();
    } catch (err) {
      console.error('Error adding reader:', err);
      setError(err.response?.data?.error || 'Failed to add that reader');
    }
  };

  const removeReader = async (reader) => {
    if (!family) return;
    if (!window.confirm(`Remove ${reader.name} from this family?`)) return;
    try {
      await familyApi.removeReaderFromFamily(family.id, reader.id);
      setError(null);
      reload();
    } catch (err) {
      console.error('Error removing reader:', err);
      setError(err.response?.data?.error || 'Failed to remove that reader');
    }
  };

  const renderJoinForm = () => (
    <form
      className="family-join"
      onSubmit={(event) => {
        event.preventDefault();
        submitCode(joinCode);
      }}
    >
      <label htmlFor="family-join-code">Invite code</label>
      <input
        id="family-join-code"
        value={joinCode}
        onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
        autoComplete="off"
      />
      <button type="submit" className="family-use">Join</button>
    </form>
  );

  const renderInviteList = () => (
    <ul className="family-people">
      {invites.map((invite) => (
        <li key={invite.id} className="family-person">
          <span>{invite.familyName}</span>
          <button type="button" className="family-text-button" onClick={() => submitCode(invite.inviteCode)}>
            Accept
          </button>
        </li>
      ))}
    </ul>
  );

  const renderRow = (row) => (
    <li key={row.key} className="family-member">
      <span className="family-member-name">
        {row.name}
        {row.isOwner && <span className="family-member-tag">Owner</span>}
        {row.streak > 0 && <span className="family-member-streak" title="Day streak">{`🔥 ${row.streak}`}</span>}
      </span>
      <span className="family-member-numbers">
        {row.hasStats ? `${row.seasonChapters.toLocaleString()} · ${row.chapters.toLocaleString()}` : ''}
      </span>
      {(row.canManage || row.canRemoveReader) ? (
        <details className="family-member-menu">
          <summary aria-label={`Actions for ${row.name}`}>⋯</summary>
          <div className="family-member-menu-panel">
            {row.canManage && (
              <>
                <button type="button" className="family-text-button" onClick={() => transferOwner({ id: row.userId, name: row.name })}>
                  Make owner
                </button>
                <button type="button" className="family-text-button" onClick={() => removeMember({ id: row.userId, name: row.name })}>
                  Remove member
                </button>
              </>
            )}
            {row.canRemoveReader && (
              <button type="button" className="family-text-button" onClick={() => removeReader({ id: row.readerId, name: row.name })}>
                Remove reader
              </button>
            )}
          </div>
        </details>
      ) : <span />}
      {row.percent != null && (
        <span className="family-member-plan">
          <span className="family-plan-track" aria-hidden="true">
            <span style={{ width: `${row.percent}%` }} />
          </span>
          <span className="family-plan-pace">{row.pace ? row.pace.value : ''}</span>
        </span>
      )}
    </li>
  );

  return (
    <div className="family-page">
      <div className="family-header">
        <h1>Family</h1>
        <div className="family-tabs" role="tablist" aria-label="Family">
          <button
            type="button"
            role="tab"
            aria-selected={!showReaders}
            className={`family-tab ${!showReaders ? 'family-tab--active' : ''}`}
            onClick={() => navigate('/family')}
          >
            Families
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={showReaders}
            className={`family-tab ${showReaders ? 'family-tab--active' : ''}`}
            onClick={() => navigate('/family#readers')}
          >
            Readers
          </button>
        </div>
      </div>

      {showReaders && <ReadersPage />}

      {!showReaders && loading && (
        <div className="family-page-pane loading">
          <LoadingSpinner size="large" message="Loading families..." />
        </div>
      )}

      {!showReaders && !loading && error && (
        <div className="error-message">
          <p>{error}</p>
          <button type="button" className="retry-button" onClick={reload}>Try Again</button>
        </div>
      )}

      {!showReaders && !loading && !error && !family && (
        <>
          <div className="family-section family-empty">
            <h2>No family yet</h2>
            <p>Create a family or join one with an invite code.</p>
            <button type="button" className="family-text-button" onClick={() => setShowCreate(true)}>
              Create a family
            </button>
          </div>
          <section className="family-section" aria-label="Join a family">
            <h2>Join</h2>
            {invites.length > 0 && renderInviteList()}
            {renderJoinForm()}
          </section>
        </>
      )}

      {!showReaders && !loading && family && (
        <>
          {invites.length > 0 && (
            <section className="family-invites-banner" aria-label="Family invites">
              {renderInviteList()}
            </section>
          )}

          <div className="progress-context">
            {families.length > 1 ? (
              <details key={family.id} className="progress-reader-picker">
                <summary className="progress-context-reader">{family.name}</summary>
                <div className="progress-reader-picker-panel">
                  {families.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="family-pick"
                      onClick={() => {
                        setSelectedFamilyId(item.id);
                        setShowEditor(false);
                        setShowInvite(false);
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </details>
            ) : (
              <span className="progress-context-reader">{family.name}</span>
            )}
            <div className="family-context-actions">
              <span>{isOwner ? 'You own this family' : `Owned by ${ownerName}`}</span>
              <button
                type="button"
                className="family-text-button"
                onClick={() => {
                  setShowInvite((open) => !open);
                  setInviteNote('');
                }}
              >
                Invite
              </button>
            </div>
          </div>

          {showInvite && (
            <section className="family-section" aria-label="Invite">
              <div className="family-invite-code">
                <code>{family.inviteCode}</code>
                <button type="button" className="family-text-button" onClick={copyInviteCode}>
                  {inviteNote || 'Copy'}
                </button>
              </div>
              <label className="family-search" htmlFor="family-people-search">Find someone</label>
              <input
                id="family-people-search"
                value={inviteQuery}
                onChange={(event) => setInviteQuery(event.target.value)}
                placeholder="Name or email"
              />
              <ul className="family-people">
                {inviteResults.map((person) => (
                  <li key={person.id} className="family-person">
                    <span>{person.name}</span>
                    <button type="button" className="family-text-button" onClick={() => invitePerson(person)}>
                      Invite
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="family-section">
            {!planReady && <LoadingSpinner message="Loading the plan..." />}
            {planReady && statsError && <p className="family-plan-note">{statsError}</p>}
            {planReady && totals && (
              <ul className="progress-summary-chips family-stats" aria-label="Family reading">
                <li className="progress-chip">
                  <span className="progress-chip-value">{totals.chapters.toLocaleString()}</span>
                  <span className="progress-chip-label">chapters</span>
                </li>
                <li className="progress-chip">
                  <span className="progress-chip-value">{totals.seasonChapters.toLocaleString()}</span>
                  <span className="progress-chip-label">this season</span>
                </li>
                <li className="progress-chip">
                  <span className="progress-chip-value">{`${totals.coveragePercent}%`}</span>
                  <span className="progress-chip-label">of the Bible</span>
                </li>
                <li className="progress-chip">
                  <span className="progress-chip-value">{`${totals.readingToday} / ${totals.readers}`}</span>
                  <span className="progress-chip-label">read today</span>
                </li>
              </ul>
            )}
            {planReady && planError && <p className="family-plan-note">{planError}</p>}
            {planReady && !planError && (
              <FamilyPlanSummary
                hero={hero}
                onEdit={isOwner ? () => setShowEditor(true) : null}
              />
            )}
            {showEditor && isOwner && (
              <FamilyPlanEditor
                plan={plan?.plan}
                onSave={savePlan}
                onClose={() => setShowEditor(false)}
              />
            )}
          </section>

          <section className="family-section" aria-label="People">
            <h2>People</h2>
            <ul className="family-members">
              {rows.map(renderRow)}
            </ul>
            <details className="family-manage">
              <summary>Manage</summary>
              <div className="family-manage-panel">
                <button type="button" className="family-text-button" onClick={() => setShowAddReader((open) => !open)}>
                  Add a reader
                </button>
                {showAddReader && readersToAdd.length === 0 && (
                  <p className="family-plan-note">All your readers are already in this family.</p>
                )}
                {showAddReader && readersToAdd.length > 0 && (
                  <ul className="family-people">
                    {readersToAdd.map((reader) => (
                      <li key={reader.id} className="family-person">
                        <span>{reader.name}</span>
                        <button type="button" className="family-text-button" onClick={() => addReader(reader.id)}>
                          Add
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" className="family-text-button" onClick={() => setShowJoin((open) => !open)}>
                  Join another family
                </button>
                {showJoin && renderJoinForm()}
                <button type="button" className="family-text-button" onClick={() => setShowCreate(true)}>
                  Create a family
                </button>
                {isOwner ? (
                  <button type="button" className="family-text-button" onClick={deleteFamily}>
                    Delete family
                  </button>
                ) : (
                  <button type="button" className="family-text-button" onClick={leaveFamily}>
                    Leave family
                  </button>
                )}
              </div>
            </details>
          </section>
        </>
      )}

      {showCreate && (
        <div className="modal-overlay">
          <div className="modal-content">
            <CreateFamilyForm
              onFamilyCreated={(created) => {
                setShowCreate(false);
                if (created?.id) setSelectedFamilyId(created.id);
                reload();
              }}
              onCancel={() => setShowCreate(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyPage;
